/* Searchanise results page -- Classes tab
 * Task: https://app.clickup.com/t/86d3x6j51
 *
 * WHY THIS EXISTS
 * Jossa's mockup puts Classes between Products and Collections. Searchanise
 * only has tabs for products, collections and pages, and classes are ordinary
 * products, so the app cannot give them a tab of their own. This adds one.
 *
 * WHERE THE DATA COMES FROM
 * The Storefront API, filtered to the class product types set in Theme
 * settings → Search → "Classes tab" (On Demand Class, Workshop, ...). Two calls:
 *   - on load, a lean one that only counts matches, for the tab label
 *   - when the tab is opened, the card data (image, price, instructor tag)
 * A product with no onlineStoreUrl is not on the Online Store and would 404,
 * so it is left out of both.
 *
 * HOW THE TAB WORKS
 * The widget stays on whichever tab it had; ?view=classes on the URL puts the
 * page into classes view, which hides the widget's own results and shows ours.
 * Using our own parameter rather than ?tab= keeps the widget out of it: it
 * never sees a tab name it does not know (which renders "Nothing found"), and
 * search-overlay.js's unknown-tab redirect never fires. Clicking any widget
 * tab leaves classes view first and then lets the widget do its usual thing.
 *
 * FAILS SOFT
 * No token, no query, a failed request or zero matches: the tab never appears
 * and the page is exactly the widget's own.
 */
(function () {
    'use strict';

    var cfg = document.getElementById('sxr-classes');
    if (!cfg) return;

    var TOKEN = cfg.dataset.token || '';
    var VERSION = cfg.dataset.version || '2025-10';
    var LABEL = cfg.dataset.label || 'Classes';
    var BUTTON = cfg.dataset.button || 'View class';
    var COUNTRY = /^[A-Z]{2}$/.test(cfg.dataset.country || '') ? cfg.dataset.country : null;
    var TYPES = (cfg.dataset.types || '')
        .split(',')
        .map(function (t) { return t.trim(); })
        .filter(Boolean);

    var PARAM = 'view';
    var VIEW = 'classes';
    var VIEW_CLASS = 'sxr-view-classes'; // on <html>; the widget rewrites #snize_results' classes
    var MAX = 250; // one page of the Storefront API, the most it returns per call
    var MAX_PAGES = 8; // 2,000 products; past that the count shows "+"
    var PAGE_SIZE = 24; // the widget's own page size
    var INSTRUCTOR_PREFIX = 'Class Instructor_';

    var term = sanitise(new URLSearchParams(window.location.search).get('q') || '');
    if (!TOKEN || !TYPES.length || !term) return;

    var state = {
        count: null, // null until counted
        more: false, // more than MAX matched
        cards: null, // null until fetched
        shown: PAGE_SIZE,
        loading: false,
    };

    /* ----------------------------------------------------------------------
       Storefront API
       ---------------------------------------------------------------------- */

    /* Storefront search syntax treats quotes, parentheses, colons and the
       boolean words as operators. A shopper typing any of them should get a
       plain search, not a syntax error or someone else's filter. */
    function sanitise(q) {
        return q
            .replace(/[()":\\*]/g, ' ')
            .replace(/\b(AND|OR|NOT)\b/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 100);
    }

    function searchString() {
        var types = TYPES.map(function (t) {
            return 'product_type:"' + t.replace(/"/g, '') + '"';
        }).join(' OR ');
        return '(' + types + ') AND ' + term;
    }

    /* Walks the pages until the results run out (or MAX_PAGES), so the tab
       shows the exact number of classes rather than "140+". "Is there a way we
       could also show the exact number of classes instead of an estimate"
       (Jossa, 09/18). The "+" only comes back past MAX_PAGES. */
    function request(fields) {
        var nodes = [];
        function next(after, page) {
            return requestPage(fields, after).then(function (res) {
                nodes = nodes.concat(res.nodes);
                if (res.more && res.cursor && page < MAX_PAGES) return next(res.cursor, page + 1);
                return { nodes: nodes, more: res.more };
            });
        }
        return next(null, 1);
    }

    function requestPage(fields, after) {
        var query =
            'query SxrClasses($q: String!, $first: Int!, $after: String' + (COUNTRY ? ', $country: CountryCode' : '') + ')' +
            (COUNTRY ? ' @inContext(country: $country)' : '') +
            ' { products(query: $q, first: $first, after: $after, sortKey: RELEVANCE) { nodes { ' + fields + ' } pageInfo { hasNextPage endCursor } } }';
        var variables = { q: searchString(), first: MAX, after: after };
        if (COUNTRY) variables.country = COUNTRY;

        return fetch('/api/' + VERSION + '/graphql.json', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': TOKEN,
            },
            body: JSON.stringify({ query: query, variables: variables }),
        })
            .then(function (r) {
                if (!r.ok) throw new Error('HTTP ' + r.status);
                return r.json();
            })
            .then(function (payload) {
                if (payload.errors && payload.errors.length) throw new Error(payload.errors[0].message);
                var products = payload.data.products;
                return {
                    nodes: products.nodes.filter(function (n) { return n.onlineStoreUrl; }),
                    more: products.pageInfo.hasNextPage,
                    cursor: products.pageInfo.endCursor,
                };
            });
    }

    function countClasses() {
        return request('onlineStoreUrl').then(function (res) {
            state.count = res.nodes.length;
            state.more = res.more;
        });
    }

    function loadCards() {
        if (state.cards || state.loading) return;
        state.loading = true;
        render();
        request(
            'title onlineStoreUrl tags ' +
                'featuredImage { url(transform: { maxWidth: 600, maxHeight: 600 }) altText } ' +
                'priceRange { minVariantPrice { amount currencyCode } } ' +
                'compareAtPriceRange { minVariantPrice { amount currencyCode } }',
        )
            .then(function (res) {
                state.cards = res.nodes;
                state.count = res.nodes.length;
                state.more = res.more;
            })
            .catch(function () {
                state.cards = [];
            })
            .then(function () {
                state.loading = false;
                if (!state.cards.length) leaveView();
                sync();
            });
    }

    /* ----------------------------------------------------------------------
       View state
       ---------------------------------------------------------------------- */

    function inView() {
        return new URLSearchParams(window.location.search).get(PARAM) === VIEW;
    }

    function urlWithView(on) {
        var params = new URLSearchParams(window.location.search);
        if (on) params.set(PARAM, VIEW);
        else params.delete(PARAM);
        var qs = params.toString();
        return window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    }

    function enterView() {
        if (!inView()) window.history.pushState(null, '', urlWithView(true));
        sync();
    }

    function leaveView() {
        if (inView()) window.history.replaceState(null, '', urlWithView(false));
        document.documentElement.classList.remove(VIEW_CLASS);
    }

    /* ----------------------------------------------------------------------
       Rendering
       ---------------------------------------------------------------------- */

    function escapeHtml(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    function money(price) {
        if (!price) return '';
        var amount = parseFloat(price.amount);
        if (!amount) return 'Free';
        try {
            return new Intl.NumberFormat(document.documentElement.lang || 'en', {
                style: 'currency',
                currency: price.currencyCode,
            }).format(amount);
        } catch (e) {
            return price.amount + ' ' + price.currencyCode;
        }
    }

    /* Relative, so the link stays on whichever domain the shopper is on. */
    function path(url) {
        try {
            var u = new URL(url);
            return u.pathname + u.search;
        } catch (e) {
            return url;
        }
    }

    function instructors(tags) {
        return (tags || [])
            .filter(function (t) { return t.indexOf(INSTRUCTOR_PREFIX) === 0; })
            .map(function (t) { return t.slice(INSTRUCTOR_PREFIX.length).trim(); })
            .filter(Boolean);
    }

    function cardMarkup(node) {
        var href = escapeHtml(path(node.onlineStoreUrl));
        var title = escapeHtml(node.title);
        var img = node.featuredImage;
        var names = instructors(node.tags);
        var price = node.priceRange && node.priceRange.minVariantPrice;
        var compare = node.compareAtPriceRange && node.compareAtPriceRange.minVariantPrice;
        var onSale = compare && price && parseFloat(compare.amount) > parseFloat(price.amount);

        return (
            '<li class="sxr-class">' +
            '<a class="sxr-class__media" href="' + href + '" tabindex="-1" aria-hidden="true">' +
            (img
                ? '<img src="' + escapeHtml(img.url) + '" alt="' + escapeHtml(img.altText || '') + '" loading="lazy" width="600" height="600">'
                : '') +
            '</a>' +
            '<div class="sxr-class__body">' +
            '<h3 class="sxr-class__title"><a href="' + href + '">' + title + '</a></h3>' +
            (names.length ? '<p class="sxr-class__instructor">with ' + escapeHtml(names.join(' & ')) + '</p>' : '') +
            '<p class="sxr-class__price">' +
            (onSale
                ? '<span class="sxr-class__price-sale">' + money(price) + '</span> <s>' + money(compare) + '</s>'
                : money(price)) +
            '</p>' +
            '</div>' +
            '<a class="sxr-class__button" href="' + href + '">' + escapeHtml(BUTTON) + '</a>' +
            '</li>'
        );
    }

    function countLabel() {
        return state.count + (state.more ? '+' : '');
    }

    function ensureTab() {
        var ul = document.querySelector('#snize_results .snize-main-panel-controls ul');
        if (!ul) return null;

        var link = ul.querySelector('.sxr-classes-tab');
        if (!link) {
            var li = document.createElement('li');
            li.className = 'sxr-classes-tab-item';
            li.hidden = true;
            link = document.createElement('a');
            link.className = 'sxr-classes-tab';
            link.href = urlWithView(true);
            // The widget's row is a tablist of role="tab" links; an unmarked
            // link inside it breaks the list for screen readers.
            link.setAttribute('role', 'tab');
            link.setAttribute('aria-selected', 'false');
            link.innerHTML = escapeHtml(LABEL) + ' <span></span>';
            li.appendChild(link);

            var products = ul.querySelector('.snize-main-panel-controls-products');
            var after = products && products.closest('li');
            if (after && after.nextSibling) ul.insertBefore(li, after.nextSibling);
            else ul.appendChild(li);
        }

        var item = link.parentElement;
        var visible = state.count !== null && state.count > 0;
        if (item.hidden === visible) item.hidden = !visible;
        var span = link.querySelector('span');
        if (visible && span.textContent !== countLabel()) span.textContent = countLabel();
        return link;
    }

    function ensurePanel() {
        var results = document.getElementById('snize_results');
        if (!results) return null;
        var panel = document.getElementById('sxr-classes-panel');
        if (!panel || panel.parentElement !== results) {
            if (panel) panel.remove();
            panel = document.createElement('div');
            panel.id = 'sxr-classes-panel';
            panel.className = 'sxr-classes';
            results.appendChild(panel);
        }
        return panel;
    }

    function render() {
        var panel = ensurePanel();
        if (!panel) return;

        var html;
        if (state.loading || !state.cards) {
            html = '<p class="sxr-classes__status" role="status">Loading classes…</p>';
        } else {
            var shown = state.cards.slice(0, state.shown);
            html =
                '<ul class="sxr-classes__grid">' + shown.map(cardMarkup).join('') + '</ul>' +
                (state.cards.length > state.shown
                    ? '<div class="sxr-classes__more"><button type="button" class="sxr-classes__more-button">Show more</button></div>'
                    : '');
        }

        if (panel.dataset.rendered !== html) {
            panel.dataset.rendered = html;
            panel.innerHTML = html;
        }
    }

    /* Writes only on a change, so the observer below doesn't loop. */
    function setState(tab, selected) {
        var value = selected ? 'true' : 'false';
        if (tab.getAttribute('aria-selected') !== value) tab.setAttribute('aria-selected', value);
        if (selected && tab.getAttribute('aria-current') !== 'page') tab.setAttribute('aria-current', 'page');
        else if (!selected && tab.hasAttribute('aria-current')) tab.removeAttribute('aria-current');
    }

    /* Idempotent: runs on every widget redraw, and only writes when something
       actually differs, so its own writes settle instead of looping. */
    function sync() {
        var link = ensureTab();
        var on = inView() && state.count !== 0;

        document.documentElement.classList.toggle(VIEW_CLASS, on);
        if (link) {
            link.classList.toggle('active', on);
            setState(link, on);
            /* In classes view the widget still marks its own tab selected, so
               two tabs would read as selected. Ours wins; the widget's comes
               back when the view is left (the widget may not redraw then). */
            var tabs = link.closest('ul').querySelectorAll('a[role="tab"]:not(.sxr-classes-tab)');
            for (var i = 0; i < tabs.length; i++) {
                setState(tabs[i], !on && tabs[i].classList.contains('active'));
            }
        }

        if (on) {
            loadCards();
            render();
        }
    }

    /* ----------------------------------------------------------------------
       Events
       ---------------------------------------------------------------------- */

    document.addEventListener(
        'click',
        function (event) {
            var target = event.target;
            if (!target.closest) return;

            if (target.closest('.sxr-classes-tab')) {
                event.preventDefault();
                enterView();
                return;
            }

            if (target.closest('.sxr-classes__more-button')) {
                state.shown += PAGE_SIZE;
                render();
                return;
            }

            // Any widget tab: leave classes view, then let the widget handle it.
            if (inView() && target.closest('#snize_results .snize-main-panel-controls a')) {
                leaveView();
            }
        },
        true,
    );

    window.addEventListener('popstate', sync);

    var queued = false;
    function queue() {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
            queued = false;
            sync();
        });
    }

    function boot() {
        new MutationObserver(queue).observe(document.body, { childList: true, subtree: true });
        countClasses()
            .catch(function () {
                state.count = 0;
                leaveView();
            })
            .then(sync);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();
